/**
 * TrialExpired.tsx — Shown when a customer's trial has ended without a payment method.
 * Route: /trial-expired
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockClockIcon from '@mui/icons-material/LockClock';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';

export default function TrialExpired() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0d1117 0%, #0a1929 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      {/* Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
        <SecurityIcon sx={{ color: '#00E5FF', fontSize: 32, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
        <Typography variant="h5" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
      </Box>

      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 520,
          borderRadius: 3,
          border: '1px solid rgba(247,127,0,0.25)',
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'rgba(247,127,0,0.1)',
            border: '2px solid rgba(247,127,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <LockClockIcon sx={{ fontSize: 36, color: '#f77f00' }} />
        </Box>

        <Chip
          label="Trial Ended"
          size="small"
          sx={{ bgcolor: 'rgba(247,127,0,0.12)', color: '#f77f00', fontWeight: 700, mb: 2 }}
        />

        <Typography variant="h4" fontWeight={800} mb={2}>
          Your free trial has ended
        </Typography>

        <Typography variant="body1" color="text.secondary" mb={4} sx={{ lineHeight: 1.7 }}>
          Add a payment method to reactivate your Shield account and continue
          protecting your cloud infrastructure.
        </Typography>

        {/* Data safety notice */}
        <Box
          sx={{
            p: 2,
            mb: 4,
            borderRadius: 2,
            bgcolor: 'rgba(0,180,216,0.06)',
            border: '1px solid rgba(0,180,216,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            textAlign: 'left',
          }}
        >
          <StorageIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary">
            <strong style={{ color: '#e6edf3' }}>Your data is safe.</strong>{' '}
            We preserve all your scans, findings, and history for 30 days
            after trial expiry.
          </Typography>
        </Box>

        <Stack spacing={2}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<CreditCardIcon />}
            onClick={() => navigate('/billing')}
            sx={{ fontWeight: 700, py: 1.5, fontSize: 16 }}
          >
            Reactivate Account →
          </Button>

          <Typography variant="body2" color="text.disabled">
            Questions?{' '}
            <Link
              href="mailto:support@shield.io"
              underline="hover"
              sx={{ color: 'primary.main', cursor: 'pointer' }}
            >
              Contact us
            </Link>
          </Typography>
        </Stack>
      </Paper>

      <Typography variant="caption" color="text.disabled" mt={3}>
        No credit card required during trial · Cancel any time
      </Typography>
    </Box>
  );
}
